import type { StudentRegistrationRecordsDataItem } from "@oneapp/data";
import { SCORE_LANDING_PAGE_FALLBACK } from "./scoreLandingPageResponse";
import type {
  ScoreLandingPageResponse,
  ScoreLandingSyllabus,
} from "./scoreLandingPage.types";

export type ScoreSyllabusSlotLike = {
  grade?: string | number | null;
  gradename?: string | null;
  examtype?: string | null;
};

function normalizeExamKey(value: string | null | undefined): string {
  return String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function resolveGradeNumber(slot: ScoreSyllabusSlotLike): number {
  const fromGrade = Number(slot.grade);
  if (Number.isFinite(fromGrade) && fromGrade > 0) return fromGrade;

  const fromName = String(slot.gradename ?? "").match(/\d+/);
  return fromName ? Number(fromName[0]) : NaN;
}

export function findScoreLandingSyllabusWidget(
  scoreLandingPage: ScoreLandingPageResponse | undefined | null,
  fallback: ScoreLandingPageResponse = SCORE_LANDING_PAGE_FALLBACK,
): ScoreLandingSyllabus | undefined {
  return (
    (scoreLandingPage?.orderedWidgets?.find(
      (widget) => widget.id === "syllabus",
    )?.response as ScoreLandingSyllabus | undefined) ??
    (fallback.orderedWidgets.find((widget) => widget.id === "syllabus")
      ?.response as ScoreLandingSyllabus | undefined)
  );
}

export function resolveScoreSyllabusPdfLink(
  slot: ScoreSyllabusSlotLike,
  syllabus?: ScoreLandingSyllabus | null,
): string | undefined {
  if (!syllabus?.aboutTest?.details?.length) return undefined;

  const grade = resolveGradeNumber(slot);
  if (!Number.isFinite(grade)) return undefined;

  const gradeDetail = syllabus.aboutTest.details.find(
    (detail) => detail.grade === grade,
  );
  const examKey = normalizeExamKey(slot.examtype);
  const examDetail =
    gradeDetail?.exams.find(
      (exam) => normalizeExamKey(exam.exam) === examKey,
    ) ?? gradeDetail?.exams[0];
  const pdfLink = examDetail?.syllabus?.pdfLink?.trim();

  return pdfLink || undefined;
}

export function attachSyllabusPdfLinks(
  records: StudentRegistrationRecordsDataItem[] | undefined,
  syllabus?: ScoreLandingSyllabus | null,
): StudentRegistrationRecordsDataItem[] | undefined {
  if (!records?.length || !syllabus?.aboutTest?.details?.length) return records;

  return records.map((record) => {
    const pdfLink = resolveScoreSyllabusPdfLink(record, syllabus);
    return pdfLink ? { ...record, pdfLink } : record;
  });
}
